import { useState } from "react";
import { AdminLayout } from "@/components/layout/AdminLayout";
import { Card, CardContent } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Switch } from "@/components/ui/switch";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import {
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from "@/components/ui/tooltip";
import {
  AlertTriangle,
  Star,
  ChevronLeft,
  ChevronRight,
  ArrowUpDown,
  Mail,
} from "lucide-react";
import { trpc } from "@/lib/trpc";

type SortBy = "avgScore" | "totalRatings" | "lastRatingAt";
type SortOrder = "asc" | "desc";

function StarRating({ score }: { score: number }) {
  return (
    <div className="flex items-center gap-1">
      {[1, 2, 3, 4, 5].map((i) => (
        <Star
          key={i}
          className={`h-3.5 w-3.5 ${
            i <= Math.round(score)
              ? "fill-yellow-400 text-yellow-400"
              : "text-muted-foreground/30"
          }`}
        />
      ))}
      <span className="text-sm font-medium ml-1">{score.toFixed(1)}</span>
    </div>
  );
}

export default function ParticulierQuality() {
  const [page, setPage] = useState(1);
  const [atRiskOnly, setAtRiskOnly] = useState(false);
  const [sortBy, setSortBy] = useState<SortBy>("avgScore");
  const [sortOrder, setSortOrder] = useState<SortOrder>("asc");
  const [selectedUserId, setSelectedUserId] = useState<string | null>(null);
  const limit = 25;

  const { data, isLoading } = trpc.admin.particulierQuality.list.useQuery(
    { page, limit, atRiskOnly, sortBy, sortOrder },
    { keepPreviousData: true }
  );

  const { data: ratingDetails, isLoading: detailsLoading } =
    trpc.admin.particulierQuality.getRatingDetails.useQuery(
      { particulierUserId: selectedUserId! },
      { enabled: !!selectedUserId }
    );

  function toggleSort(col: SortBy) {
    if (sortBy === col) {
      setSortOrder((o) => (o === "asc" ? "desc" : "asc"));
    } else {
      setSortBy(col);
      setSortOrder(col === "avgScore" ? "asc" : "desc");
    }
    setPage(1);
  }

  const items = data?.items ?? [];
  const total = data?.total ?? 0;
  const totalPages = Math.max(1, Math.ceil(total / limit));

  function topTags(tagFreq: Record<string, number>): string[] {
    return Object.entries(tagFreq)
      .sort((a, b) => b[1] - a[1])
      .slice(0, 3)
      .map(([tag]) => tag);
  }

  return (
    <AdminLayout>
      <div className="space-y-6">
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-2xl font-bold">Qualite particuliers</h1>
            <p className="text-sm text-muted-foreground mt-1">
              Scores de qualite, tags frequents et comptes a risque
            </p>
          </div>
          <div className="flex items-center gap-2">
            <Switch
              id="at-risk-filter"
              checked={atRiskOnly}
              onCheckedChange={(v) => {
                setAtRiskOnly(v);
                setPage(1);
              }}
            />
            <label
              htmlFor="at-risk-filter"
              className="text-sm font-medium cursor-pointer"
            >
              Comptes a risque uniquement
            </label>
          </div>
        </div>

        {isLoading ? (
          <div className="text-center text-muted-foreground text-sm py-20">
            Chargement...
          </div>
        ) : items.length === 0 ? (
          <Card>
            <CardContent className="py-16 text-center text-muted-foreground text-sm">
              {atRiskOnly
                ? "Aucun compte a risque detecte"
                : "Aucun particulier avec des notations"}
            </CardContent>
          </Card>
        ) : (
          <>
            <Card>
              <CardContent className="p-0">
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>Particulier</TableHead>
                      <TableHead>
                        <Button
                          variant="ghost"
                          size="sm"
                          className="h-auto p-0 font-medium"
                          onClick={() => toggleSort("avgScore")}
                        >
                          Score moyen
                          <ArrowUpDown className="h-3.5 w-3.5 ml-1" />
                        </Button>
                      </TableHead>
                      <TableHead>
                        <Button
                          variant="ghost"
                          size="sm"
                          className="h-auto p-0 font-medium"
                          onClick={() => toggleSort("totalRatings")}
                        >
                          Nb ratings
                          <ArrowUpDown className="h-3.5 w-3.5 ml-1" />
                        </Button>
                      </TableHead>
                      <TableHead>Tags frequents</TableHead>
                      <TableHead>
                        <Button
                          variant="ghost"
                          size="sm"
                          className="h-auto p-0 font-medium"
                          onClick={() => toggleSort("lastRatingAt")}
                        >
                          Dernier rating
                          <ArrowUpDown className="h-3.5 w-3.5 ml-1" />
                        </Button>
                      </TableHead>
                      <TableHead>Statut</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {items.map((item: any) => (
                      <TableRow
                        key={item.particulierUserId}
                        className="cursor-pointer hover:bg-muted/50"
                        onClick={() =>
                          setSelectedUserId(item.particulierUserId)
                        }
                      >
                        <TableCell>
                          <div>
                            <div className="font-medium text-sm">
                              {item.particulierName || "Sans nom"}
                            </div>
                            <div className="text-xs text-muted-foreground">
                              {item.particulierEmail || "—"}
                            </div>
                          </div>
                        </TableCell>
                        <TableCell>
                          <StarRating score={item.avgScore} />
                        </TableCell>
                        <TableCell className="text-sm">
                          {item.totalRatings}
                        </TableCell>
                        <TableCell>
                          <div className="flex flex-wrap gap-1">
                            {topTags(item.tagFrequency).map((tag) => (
                              <Badge
                                key={tag}
                                variant="outline"
                                className="text-xs"
                              >
                                {tag}
                              </Badge>
                            ))}
                          </div>
                        </TableCell>
                        <TableCell className="text-xs text-muted-foreground">
                          {item.lastRatingAt
                            ? new Date(item.lastRatingAt).toLocaleDateString(
                                "fr-FR",
                                {
                                  day: "2-digit",
                                  month: "short",
                                  year: "numeric",
                                }
                              )
                            : "—"}
                        </TableCell>
                        <TableCell>
                          {item.isAtRisk ? (
                            <Tooltip>
                              <TooltipTrigger asChild>
                                <Badge
                                  variant="destructive"
                                  className="gap-1"
                                >
                                  <AlertTriangle className="h-3 w-3" />
                                  A risque
                                </Badge>
                              </TooltipTrigger>
                              <TooltipContent>
                                3+ signalements avec score &lt; 3/5 — action
                                recommandee
                              </TooltipContent>
                            </Tooltip>
                          ) : (
                            <Badge variant="secondary">Normal</Badge>
                          )}
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </CardContent>
            </Card>

            {totalPages > 1 && (
              <div className="flex items-center justify-between">
                <p className="text-sm text-muted-foreground">
                  {total} particulier{total > 1 ? "s" : ""} — page {page}/
                  {totalPages}
                </p>
                <div className="flex gap-2">
                  <Button
                    variant="outline"
                    size="sm"
                    disabled={page <= 1}
                    onClick={() => setPage((p) => p - 1)}
                  >
                    <ChevronLeft className="h-4 w-4" />
                  </Button>
                  <Button
                    variant="outline"
                    size="sm"
                    disabled={page >= totalPages}
                    onClick={() => setPage((p) => p + 1)}
                  >
                    <ChevronRight className="h-4 w-4" />
                  </Button>
                </div>
              </div>
            )}
          </>
        )}
      </div>

      <Dialog
        open={!!selectedUserId}
        onOpenChange={(open) => {
          if (!open) setSelectedUserId(null);
        }}
      >
        <DialogContent className="max-w-lg max-h-[80vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>Detail des notations</DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            {selectedUserId && (
              <>
                {(() => {
                  const selected = items.find(
                    (i: any) => i.particulierUserId === selectedUserId
                  );
                  if (!selected) return null;
                  return (
                    <div className="space-y-2 pb-4 border-b">
                      <div className="font-medium">
                        {selected.particulierName || "Sans nom"}
                      </div>
                      <div className="text-sm text-muted-foreground">
                        {selected.particulierEmail || "—"}
                      </div>
                      <div className="flex items-center gap-3">
                        <StarRating score={selected.avgScore} />
                        <span className="text-xs text-muted-foreground">
                          ({selected.totalRatings} notation
                          {selected.totalRatings > 1 ? "s" : ""})
                        </span>
                      </div>
                      {selected.particulierEmail && (
                        <Button
                          variant="outline"
                          size="sm"
                          className="mt-2"
                          onClick={() =>
                            window.open(
                              `mailto:${selected.particulierEmail}`,
                              "_blank"
                            )
                          }
                        >
                          <Mail className="h-3.5 w-3.5 mr-1" />
                          Contacter
                        </Button>
                      )}
                    </div>
                  );
                })()}

                {detailsLoading ? (
                  <div className="text-center text-muted-foreground text-sm py-8">
                    Chargement...
                  </div>
                ) : !ratingDetails || ratingDetails.length === 0 ? (
                  <div className="text-center text-muted-foreground text-sm py-8">
                    Aucune notation
                  </div>
                ) : (
                  <div className="space-y-3">
                    {ratingDetails.map((r: any) => (
                      <Card key={r.id}>
                        <CardContent className="pt-4 pb-3 space-y-2">
                          <div className="flex items-center justify-between">
                            <StarRating score={r.score} />
                            <span className="text-xs text-muted-foreground">
                              {new Date(r.createdAt).toLocaleDateString(
                                "fr-FR",
                                {
                                  day: "2-digit",
                                  month: "short",
                                  year: "numeric",
                                }
                              )}
                            </span>
                          </div>
                          {r.tags.length > 0 && (
                            <div className="flex flex-wrap gap-1">
                              {r.tags.map((tag: string) => (
                                <Badge
                                  key={tag}
                                  variant="outline"
                                  className="text-xs"
                                >
                                  {tag}
                                </Badge>
                              ))}
                            </div>
                          )}
                          {r.comment && (
                            <p className="text-sm text-muted-foreground">
                              {r.comment}
                            </p>
                          )}
                        </CardContent>
                      </Card>
                    ))}
                  </div>
                )}
              </>
            )}
          </div>
        </DialogContent>
      </Dialog>
    </AdminLayout>
  );
}
